import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, { useCallback, useState } from 'react';
import { RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getEmployeeTimeOffBalances } from '../../services/employeeTimeOffBalanceService';
import type { EmployeeTimeOffBalances } from '../../types/employeeTimeOffBalance';
import { normalizeApiError } from '../../api/apiError';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatLeaveBalanceValue } from '../../utils/leaveSession';
import { leaveBalanceDisplayColor } from '../../utils/leaveBalance';

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeTimeOffBalance'>;
const now = new Date();

export function EmployeeTimeOffBalanceScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const canUpdateLeave =
    auth.roles?.includes('HR_ADMIN') === true && auth.hasPermission(PERMISSIONS.SETTINGS_UPDATE);
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [data, setData] = useState<EmployeeTimeOffBalances | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (refresh = false) => {
    const selectedYear = Number(year);
    const selectedMonth = Number(month);
    if (
      !Number.isInteger(selectedYear) ||
      selectedYear < 2000 ||
      selectedYear > 2200 ||
      !Number.isInteger(selectedMonth) ||
      selectedMonth < 1 ||
      selectedMonth > 12
    ) {
      setError('Enter a valid year and month (1–12).');
      setLoading(false);
      return;
    }
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      setData(await getEmployeeTimeOffBalances(route.params.employeeId, selectedYear, selectedMonth));
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [month, route.params.employeeId, year]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  return (
    <AppScreen
      scroll
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => load(true)} />}
    >
      <ScreenHeader title="Employee balances" onBack={navigation.goBack} />
      <View style={styles.body}>
        <View style={styles.filters}>
          <AppInput label="Year" value={year} onChangeText={setYear} keyboardType="number-pad" />
          <AppInput label="Month" value={month} onChangeText={setMonth} keyboardType="number-pad" />
        </View>
        <AppButton title="View balances" variant="secondary" onPress={() => load()} />
        {loading ? (
          <AppLoader message="Loading balances" />
        ) : error && !data ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : data &&
          !data.leave.length &&
          !data.wfh &&
          (!isTimePermissionEnabled() || !data.permissions.length) ? (
          <EmptyState
            title="No data available"
            message="No leave, WFH or permission balance exists for the selected period."
          />
        ) : data ? (
          <>
            <Text style={[styles.heading, { color: palette.primary }]}>Leave balance</Text>
            {data.leave.length ? (
              data.leave.map(item => (
                <AppCard key={item.employeeLeaveBalanceId} style={styles.card}>
                  <Text style={[styles.title, { color: palette.textPrimary }]}>{formatDisplayLabel(item.leaveTypeName)}</Text>
                  <Text
                    style={{
                      color: leaveBalanceDisplayColor(
                        item.availableBalance,
                        palette.textSecondary,
                        palette.error,
                      ),
                    }}
                  >
                    Available: {formatLeaveBalanceValue(item.availableBalance)} · Pending:{' '}
                    {formatLeaveBalanceValue(item.pendingLeaveDays)} · Used:{' '}
                    {formatLeaveBalanceValue(item.debitedDays)}
                  </Text>
                  {item.annualEntitlement != null || item.monthlyAccrual != null ? (
                    <Text style={{ color: palette.textSecondary }}>
                      {[
                        item.annualEntitlement != null ? `Entitlement ${item.annualEntitlement}` : null,
                        item.monthlyAccrual != null ? `Accrual ${item.monthlyAccrual}/month` : null,
                      ].filter(Boolean).join(' · ')}
                    </Text>
                  ) : null}
                  {canUpdateLeave ? (
                    <AppButton
                      title="Update leave balance"
                      variant="secondary"
                      onPress={() =>
                        navigation.navigate(routes.leaveBalanceUpdate, {
                          employeeId: route.params.employeeId,
                          leaveTypeId: item.leaveTypeId,
                          leaveYear: item.leaveYear || Number(year),
                        })
                      }
                    />
                  ) : null}
                </AppCard>
              ))
            ) : (
              <EmptyState
                title="No data available"
                message="No leave balance exists for the selected year."
              />
            )}
            <Text style={[styles.heading, { color: palette.primary }]}>WFH balance</Text>
            {data.wfh ? (
              <AppCard style={styles.card}>
                <Text style={[styles.title, { color: palette.textPrimary }]}>{data.wfh.policyName}</Text>
                <Text style={{ color: palette.textSecondary }}>
                  Month: {data.wfh.usedDaysMonth} used · {data.wfh.remainingDaysMonth} remaining
                </Text>
                <Text style={{ color: palette.textSecondary }}>
                  Year: {data.wfh.usedDaysYear} used · {data.wfh.remainingDaysYear} remaining
                </Text>
              </AppCard>
            ) : (
              <EmptyState
                title="No data available"
                message="No active WFH policy is assigned to this employee."
              />
            )}
            {isTimePermissionEnabled() ? <>
            <Text style={[styles.heading, { color: palette.primary }]}>Permission balance</Text>
            {data.permissions.length ? (
              data.permissions.map(item => (
                <AppCard key={item.permissionPolicyId} style={styles.card}>
                  <Text style={[styles.title, { color: palette.textPrimary }]}>
                    {formatDisplayLabel(item.permissionTypeName)}
                  </Text>
                  <Text style={{ color: palette.textSecondary }}>
                    Month: {item.usedMonth} used · {item.remainingMonth} remaining of{' '}
                    {item.maximumPermissionsPerMonth} overall
                  </Text>
                  <Text style={{ color: palette.textSecondary }}>
                    Year: {item.usedYear} used · {item.remainingYear} remaining of{' '}
                    {item.maximumPermissionsPerYear}
                  </Text>
                </AppCard>
              ))
            ) : (
              <EmptyState
                title="No data available"
                message="No active permission policy is configured."
              />
            )}
            </> : null}
          </>
        ) : null}
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  filters: { flexDirection: 'row', gap: spacing.sm },
  heading: { ...typography.sectionTitle, marginTop: spacing.md },
  card: { gap: spacing.xs },
  title: typography.sectionTitle,
});
